import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import type { CouncilApproval } from '@/types/api'

import {
  approveApproval,
  listApprovals,
  rejectApproval,
} from '@/lib/api/endpoints'
import { createOptimisticListMutation } from '@/lib/queries/_helpers'
import { queryKeys } from '@/lib/queries/keys'
import { toast } from '@/components/ui/sonner'

// -----------------------------------------------------------------------------
// Reads
// -----------------------------------------------------------------------------

interface UseApprovalsOptions {
  refetchInterval?: number | false
}

export function useApprovals(
  companyId: string | null,
  opts: UseApprovalsOptions = {},
) {
  return useQuery({
    queryKey: companyId
      ? queryKeys.approvals.list(companyId)
      : queryKeys.approvals.all,
    queryFn: () => listApprovals(companyId!),
    enabled: !!companyId,
    refetchInterval: opts.refetchInterval ?? false,
  })
}

// -----------------------------------------------------------------------------
// Mutations
// -----------------------------------------------------------------------------
//
// Approve / reject mexem em UM item da lista de approvals. Optimistic patch
// muda só o `status` (e o `approvedByUserId` quando o backend de auth da
// VEC-140 estiver pronto). Mantemos o item na lista — quem filtra "pending"
// é o consumidor (ex.: KPI do Dashboard).
//
// Não invalidamos audit-log aqui porque o hook `useAuditLog` ainda não foi
// criado (entra na M3). Quando entrar, basta listar a chave em
// `invalidateAlso` — o backend já registra a entrada via MSW handler.
// -----------------------------------------------------------------------------

function useApprovalDecision(
  companyId: string | null,
  fn: (id: string) => Promise<CouncilApproval>,
  optimisticStatus: CouncilApproval['status'],
) {
  const qc = useQueryClient()

  return useMutation(
    createOptimisticListMutation<CouncilApproval[], string, CouncilApproval>({
      queryClient: qc,
      queryKey: companyId
        ? queryKeys.approvals.list(companyId)
        : queryKeys.approvals.all,
      mutationFn: fn,
      applyOptimistic: (current, approvalId) =>
        (current ?? []).map((a) =>
          a.id === approvalId ? { ...a, status: optimisticStatus } : a,
        ),
      onSuccess: () => {
        const label = optimisticStatus === 'approved' ? 'aprovada' : 'rejeitada'
        toast.success(`Aprovação ${label} com sucesso.`)
      },
      onError: (error) => {
        const is404 = error.message?.includes('404') || (error as { status?: number }).status === 404
        if (is404) {
          toast.error('Endpoint de aprovação não disponível no backend.', {
            description: 'Esta funcionalidade requer integração com OpenClaw/NAVI (M4).',
          })
        } else {
          toast.error('Erro ao processar aprovação.', {
            description: error.message,
          })
        }
      },
    }),
  )
}

export function useApproveApproval(companyId: string | null) {
  return useApprovalDecision(companyId, approveApproval, 'approved')
}

export function useRejectApproval(companyId: string | null) {
  return useApprovalDecision(companyId, rejectApproval, 'rejected')
}
